import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

import { Store } from '@ngrx/store';
import { AppActions } from 'projects/semeris/src/store/app/app.actions';
import { BehaviorSubject, Subject } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

import { Memo, MemoShortName } from '../../../memo/models/memo.model';
import { MemoService } from '../../../memo/services/memo.service';
import { HintOverlayComponent } from '../../../shared/components/hint-overlay/hint-overlay.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { PermissionsDirective } from '../../../shared/directives/permissions.directive';
import { Permission } from '../../../shared/models/api/permissions.enum';
import { UserDateTimeFormatPipe } from '../../../shared/pipes/user-date-time-format.pipe';
import { ButtonDirective } from '../../../ui/button/button/button.directive';

@Component({
  selector: 'semeris-manager-approval-dropdown',
  imports: [
    HintOverlayComponent,
    MatIconModule,
    ButtonDirective,
    PermissionsDirective,
    ReactiveFormsModule,
    AsyncPipe,
    UserDateTimeFormatPipe,
    LoaderComponent,
  ],
  templateUrl: './manager-approval-dropdown.component.html',
  styleUrl: './manager-approval-dropdown.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManagerApprovalDropdownComponent {
  public readonly memoService = inject(MemoService);
  public readonly store = inject(Store);
  public readonly Permissions = Permission;

  public readonly formControl = new FormControl<string>('');

  public memo$ = new BehaviorSubject<Memo>(null);
  public submitInProgress$ = new BehaviorSubject<boolean>(false);
  private readonly trigger$ = new Subject<void>();

  constructor() {
    this.trigger$
      .pipe(
        switchMap(() => {
          return this.memoService.getMemoByShortName(MemoShortName.CatalogManagerApproval);
        }),
        takeUntilDestroyed()
      )
      .subscribe(
        (memo) => {
          this.formControl.setValue(memo.content);
          this.memo$.next(memo);
        },
        () => {
          this.memo$.next({
            content: '',
            shortName: MemoShortName.CatalogManagerApproval,
          });
        }
      );
  }

  public onOpen() {
    this.trigger$.next(undefined);
  }

  public onSave() {
    this.submitInProgress$.next(true);
    this.memoService
      .upsertMemo(MemoShortName.CatalogManagerApproval, this.formControl.value)
      .pipe(take(1))
      .subscribe(
        (memo) => {
          this.memo$.next(memo);
          this.store.dispatch(AppActions.PushNotification({ message: 'Manager approval updated', config: 'success' }));
          this.submitInProgress$.next(false);
        },
        () => {
          this.store.dispatch(
            AppActions.PushNotification({ message: 'Failed to update manager approval', config: 'fail' })
          );
          this.submitInProgress$.next(false);
        }
      );
  }
}
